<template>
	<PageLoading :loading="loading" class="relative">
		<PagePadding class="flex items-center justify-between">
			<ShieldIcon
				class="text-label-tertiary h-5 w-5"
				v-hover="{ impact: 'soft' }"
				:class="{
					'pointer-events-none opacity-0': !price && droppedItems.length
				}"
				@click="openFairness()"
			/>
			<div class="inline-flex items-center gap-2.5">
				<div
					class="font-squad text-label-primary text-center text-2xl leading-8 font-black uppercase"
				>
					{{ name }}
				</div>
				<RiskBarSquare :value="risk" v-if="risk" class="-mt-1" />
			</div>
			<ShareIcon
				class="text-label-tertiary h-5 w-5"
				v-hover="{ impact: 'soft' }"
				:class="{ 'pointer-events-none opacity-0': !price }"
				@click="share"
			/>
		</PagePadding>
		<PagePillRow class="mt-4" v-if="price">
			<BaseLabel>
				<div>{{ t('cost') }}</div>
				<CoinPrice class="ml-1.5" size="xs" :price="price" />
			</BaseLabel>
			<BaseButton
				size="sm"
				color="secondary"
				borderRounded
				@click="demoSpin()"
				:disabled="rolling"
			>
				<DemoIcon
					class="h-5 w-5"
					:class="{ 'animate-spin-once': demoIconAnimation }"
				/>
				<div class="text-xs! uppercase">{{ t('demoSpin') }}</div>
			</BaseButton>
		</PagePillRow>
		<CaseEmbed
			class="mt-3"
			:uuid="uuid"
			ref="embed"
			@loaded="loaded"
			v-model:name="name"
			v-model:price="price"
			v-model:droppedItems="droppedItems"
			v-model:rolling="rolling"
		>
			<template #overlay>
				<div
					class="absolute inset-0 flex items-center justify-center bg-black/50 backdrop-blur-xs transition-opacity duration-1000"
					:class="{ 'opacity-0': rolling }"
				>
					<Transition
						enterActiveClass="transition-transform duration-500"
						leaveActiveClass="transition-transform duration-500"
						leaveToClass="translate-y-[200%]"
						enterFromClass="translate-y-[200%]"
					>
						<LazyImage
							:src="image"
							:alt="name"
							class="animate-hover-1 h-50 w-50 transform"
							v-if="!rolling"
						/>
					</Transition>
				</div>
			</template>
			<template #live v-if="topDrop.length">
				<LiveDrop :liveDrop="topDrop" class="mb-6">{{ t('last') }}</LiveDrop>
			</template>
		</CaseEmbed>
		<PagePadding class="flex items-center gap-6">
			<div
				class="text-label-tertiary mt-6 inline-flex items-center gap-1.5"
				@click="openHistory"
				v-hover="{ active: 'scale-95', impact: 'soft' }"
				v-if="type === 'regular' && version > 1 && hasPreviousVersions"
			>
				<HistoryIcon class="h-5 w-5" />
				<div class="text-sm font-medium capitalize">
					{{ t('history') }}
				</div>
			</div>
			<div
				class="text-label-tertiary mt-6 inline-flex items-center gap-1.5"
				@click="openFairness"
				v-hover="{ active: 'scale-95', impact: 'soft' }"
			>
				<ShieldIcon class="h-5 w-5" />
				<div class="text-sm font-medium capitalize">
					{{ t('fair') }}
				</div>
			</div>
		</PagePadding>
		<PriceHistoryModal
			v-if="version > 1 && hasPreviousVersions"
			v-model="historyModal"
			:version="version"
			:uuid="uuid"
		/>
	</PageLoading>
</template>
<script setup lang="ts">
import ShieldIcon from '@/assets/icons/shield.svg'
import ShareIcon from '@/assets/icons/share.svg'
import PagePadding from '@/components/PagePadding.vue'
import { useLayout } from '@/stores/layout'
import PagePillRow from '@/components/PagePillRow.vue'
import BaseLabel from '@/components/BaseLabel.vue'
import BaseButton from '@/components/BaseButton.vue'
import DemoIcon from '@/assets/icons/demo.svg'
import { computed, ref, watch } from 'vue'
import { sleep } from '@/utils/sleep'
import LazyImage from '@/components/LazyImage.vue'
import { animation, caseIcon, icon } from '@/utils/urls'
import PageLoading from '@/components/PageLoading.vue'
import CoinPrice from '@/components/CoinPrice.vue'
import { useI18n } from 'vue-i18n'
import CaseEmbed from '@/components/CaseEmbed.vue'
import { WebApp } from '@grammyjs/web-app'
import HistoryIcon from '@/assets/icons/history.svg'
import PriceHistoryModal from '@/components/modals/PriceHistoryModal.vue'
import RiskBarSquare from '@/components/RiskBarSquare.vue'
import LiveDrop from '@/components/LiveDrop.vue'
import { useLive } from '@/stores/live'

const { t } = useI18n({
	messages: {
		en: {
			cost: 'сost',
			demoSpin: 'demo spin',
			openFor: 'open {0} for',
			risk: {
				low: 'Low risk',
				medium: 'Medium risk',
				high: 'High risk'
			},
			free: 'open for free',
			required: '{0} required',
			next: 'next in {0}',
			cta: `\n🎁 {0} - Open now on {'@'}ragegift ⤴️`,
			fair: 'Provably Fair',
			history: 'Prices History',
			last: 'Last top pulls'
		},
		ru: {
			cost: 'цена',
			demoSpin: 'демо спин',
			openFor: 'открыть {0} за',
			risk: {
				low: 'Низкий риск',
				medium: 'Средний риск',
				high: 'Высокий риск'
			},
			free: 'открыть бесплатно',
			required: 'требуется {0}',
			next: 'следующий {0}',
			cta: `\n🎁 {0} - Открой сейчас {'@'}ragegift ⤴️`,
			fair: 'Честная Игра',
			history: 'История Цен',
			last: 'последний топ дроп'
		}
	}
})

const props = defineProps<{
	uuid: string
}>()

const layout = useLayout()

layout.showBottomNavbar()

const embed = ref<InstanceType<typeof CaseEmbed>>()

const loading = ref(true)
const name = ref()
const price = ref(0)
const droppedItems = ref<
	{
		uuid: string
		src: string
		modelUuid: string
		collection: string
		name: string
		price: number
		chance: number
		preloader: string | null
		fair: {
			nonce: number
			clientSeed: string
			serverSeed: string | null
			serverSeedHash: string
			ticketsCount: number
			ticket: number
		}
		tickets: [number, number]
		claimable: boolean
	}[]
>([])

const demoIconAnimation = ref(false)
const rolling = ref(false)

const demoSpin = async () => {
	if (!embed.value) {
		return
	}

	const items = embed.value.getItems()

	;(async () => {
		demoIconAnimation.value = true
		await sleep(500)
		demoIconAnimation.value = false
	})()

	rolling.value = true

	const result = [0, 1, 2, 3].map(() => {
		const rnd = Math.random() * items[items.length - 1].ticketsRange[1]

		return items.find(
			(item) => rnd >= item.ticketsRange[0] && rnd < item.ticketsRange[1]
		)!
	})

	await embed.value.spin(
		result.map((item) => ({
			src: animation(item.modelUuid),
			imgSrc: icon(item.modelUuid),
			preloader: item.preloader || undefined,
			price: item.price,
			chance: item.chance
		}))
	)

	// showDrop.value = true
	rolling.value = false
}

const share = () => {
	const cta = t('cta', [name.value])
	const link = `https://t.me/share/url?url=${encodeURIComponent(`https://t.me/ragegiftbot/app?startapp=case-${props.uuid}`)}&text=${encodeURIComponent(cta)}`
	WebApp.openTelegramLink(link)
}

const openFairness = () => {
	if (!embed.value) {
		return
	}

	embed.value.openFairness()
}

const type = ref<'regular' | 'welcome' | 'referral' | 'reward'>('regular')
const version = ref(0)
const hasPreviousVersions = ref(false)
const risk = ref(0)
const topDrop = ref<
	{
		modelName: string
		src: string
		preloader?: string
		price: number
		time: number
	}[]
>([])

const loaded = (
	price: number,
	typeValue: 'regular' | 'welcome' | 'referral' | 'reward',
	versionValue: number,
	hasPreviousVersionsValue: boolean,
	riskValue: number,
	topDropValue: {
		collectionName: string
		modelName: string
		modelUuid: string
		preloader: string | null
		price: number
	}[]
) => {
	loading.value = false
	type.value = typeValue
	version.value = versionValue
	hasPreviousVersions.value = hasPreviousVersionsValue
	risk.value = riskValue
	topDrop.value = topDropValue.map((item, i) => ({
		modelName: item.modelName,
		src: animation(item.modelUuid),
		preloader: item.preloader || undefined,
		price: item.price,
		time: Date.now() + i
	}))
}

const image = computed(() => caseIcon(props.uuid))

const historyModal = ref(false)

const openHistory = () => {
	historyModal.value = true
}

const live = useLive()

watch(live.last, async (_, from) => {
	if (!live.last.length || from.length !== 10) {
		return
	}

	const lastDrop = live.last[0]

	if (lastDrop.caseUuid !== props.uuid) {
		return
	}

	await sleep(5000)

	topDrop.value.unshift({
		modelName: lastDrop.modelName,
		src: animation(lastDrop.modelUuid),
		preloader: lastDrop.preloader || undefined,
		price: lastDrop.price,
		time: lastDrop.time
	})

	if (topDrop.value.length > 10) {
		topDrop.value.splice(10, 1)
	}
})
</script>
